import {
  Activity, AlertTriangle, BarChart3, CheckCircle,
  Cpu, Globe, RefreshCw, Server, TrendingUp, Users, Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import api from '../../config/axiosConfig';
import usePesacoreIH from '../../stores/usePesacore';

// ─── Composants UI ────────────────────────────────────────────────────────

function MetricGauge({ label, value, warn = 70, critical = 85, unit = '%', icon: Icon }) {
  const v     = typeof value === 'number' && isFinite(value) ? value : 0;
  const color = v >= critical ? 'text-red-400'   : v >= warn ? 'text-amber-400' : 'text-emerald-400';
  const bg    = v >= critical ? 'bg-red-500'     : v >= warn ? 'bg-amber-500'   : 'bg-emerald-500';
  const ring  = v >= critical ? 'border-red-800/40' : v >= warn ? 'border-amber-800/40' : 'border-emerald-800/40';

  return (
    <div className={`bg-zinc-900/40 border ${ring} rounded-xl p-4 space-y-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={13} className="text-zinc-600" />}
          <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{label}</span>
        </div>
        <span className={`text-xl font-black font-mono ${color}`}>
          {v > 0 ? `${v.toFixed(1)}${unit}` : '—'}
        </span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${bg}`}
          style={{ width: `${Math.min(100, v)}%` }} />
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color = 'text-zinc-200', icon: Icon }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-1">{label}</p>
          <p className={`text-2xl font-black ${color}`}>{value ?? '—'}</p>
          {sub && <p className="text-[10px] text-zinc-600 mt-1">{sub}</p>}
        </div>
        {Icon && <Icon size={16} className="text-zinc-700 mt-0.5" />}
      </div>
    </div>
  );
}

// Normalise un élément service quelle que soit sa forme (string, objet partiel, etc.)
function normalizeService(s) {
  if (!s || typeof s !== 'object') return { name: String(s ?? '?'), status: 'unknown', detail: '', uptimeMin: null };
  return {
    name:      typeof s.name   === 'string' ? s.name   : (s.id ? String(s.id) : '?'),
    status:    typeof s.status === 'string' ? s.status : 'unknown',
    detail:    typeof s.detail === 'string' ? s.detail : '',
    uptimeMin: typeof s.uptimeMin === 'number' ? s.uptimeMin : null,
  };
}

function ServiceRow({ service }) {
  const svc = normalizeService(service);
  const colors = {
    healthy:  'bg-emerald-500',
    warning:  'bg-amber-500',
    error:    'bg-red-500',
    degraded: 'bg-amber-500',
    unknown:  'bg-zinc-600',
  };
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800/20 last:border-0">
      <span className={`w-2 h-2 rounded-full shrink-0 ${colors[svc.status] || 'bg-zinc-600'}`} />
      <span className="text-xs text-zinc-300 flex-1">{svc.name}</span>
      <span className="text-[10px] text-zinc-600 font-mono">{svc.detail || svc.status}</span>
      {svc.uptimeMin !== null && <span className="text-[10px] text-zinc-700">{svc.uptimeMin}m uptime</span>}
    </div>
  );
}

// Mini sparkline SVG sans dépendance externe
function Sparkline({ data = [], color = '#f97316', height = 32 }) {
  if (!data.length) return <div style={{ height }} className="bg-zinc-800/30 rounded" />;
  const max = Math.max(...data.map(d => d.count), 1);
  const w   = 100;
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = height - (d.count / max) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function SparkCard({ label, series, color, icon: Icon }) {
  const last  = series?.[series.length - 1]?.count ?? 0;
  const prev  = series?.[series.length - 2]?.count ?? 0;
  const delta = last - prev;
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={12} className="text-zinc-600" />}
          <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-black text-zinc-200">{last}</span>
          {delta !== 0 && (
            <span className={`text-[10px] ${delta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {delta > 0 ? '+' : ''}{delta}
            </span>
          )}
        </div>
      </div>
      <Sparkline data={series} color={color} />
    </div>
  );
}

// ─── Hook données backend ─────────────────────────────────────────────────

function useStatsData() {
  const [stats,    setStats]    = useState(null);
  const [services, setServices] = useState([]);
  const [series,   setSeries]   = useState({ users: [], tenants: [], parcours: [] });
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, servicesRes, usersTS, tenantsTS, parcoursTS] = await Promise.all([
        api.get('/superadmin/stats'),
        api.get('/superadmin/stats/services'),
        api.get('/superadmin/stats/timeseries?metric=users&days=14'),
        api.get('/superadmin/stats/timeseries?metric=tenants&days=14'),
        api.get('/superadmin/stats/timeseries?metric=parcours&days=14'),
      ]);
      setStats(statsRes.data);
      setServices(servicesRes.data.services || []);
      setSeries({
        users:    usersTS.data.series    || [],
        tenants:  tenantsTS.data.series  || [],
        parcours: parcoursTS.data.series || [],
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return { stats, services, series, loading, error, fetchAll };
}

// ─── Panneau principal ────────────────────────────────────────────────────

export default function PanneauStats() {
  // ── Store : métriques temps réel via socket ──────────────────────────
  const {
    systemMetrics,   // { cpu, memory, disk, network, queueDepth } — mis à jour via socket
    systemHealth,    // { score, status, issues }
    liveServices,    // services socket live
    socketConnected,
    getMetricHistory,
  } = usePesacoreIH();

  // ── Backend : agrégats métier ────────────────────────────────────────
  const { stats, services, series, loading, error, fetchAll } = useStatsData();

  // Historique CPU/mémoire depuis le moteur (30 points)
  const cpuHistory  = getMetricHistory?.('cpu',    30) || [];
  const memHistory  = getMetricHistory?.('memory', 30) || [];

  // Auto-refresh toutes les 30s
  const timerRef = useRef(null);
  useEffect(() => {
    timerRef.current = setInterval(fetchAll, 30_000);
    return () => clearInterval(timerRef.current);
  }, []);

  const { kpis, growth, distribution, system } = stats || {};

  return (
    <div className="space-y-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-orange-300 tracking-tighter">TÉLÉMÉTRIE & STATS</h2>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`w-1.5 h-1.5 rounded-full ${socketConnected ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-600'}`} />
            <span className="text-[10px] text-zinc-600 uppercase tracking-widest">
              {socketConnected ? 'Socket connecté — métriques live' : 'Socket déconnecté'}
            </span>
          </div>
        </div>
        <button onClick={fetchAll} disabled={loading}
          className="flex items-center gap-1.5 p-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-500 hover:text-zinc-300 disabled:opacity-40">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-950/30 border border-red-800/30 rounded-xl text-red-300 text-xs flex gap-2">
          <AlertTriangle size={14} />{error}
        </div>
      )}

      {/* ── Métriques temps réel — depuis le store socket ── */}
      <section className="space-y-2">
        <p className="text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2">
          <Activity size={11} /> Métriques temps réel
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricGauge label="CPU"      value={systemMetrics.cpu}        icon={Cpu}      warn={70} critical={85} />
          <MetricGauge label="Mémoire"  value={systemMetrics.memory}     icon={Server}   warn={75} critical={90} />
          <MetricGauge label="Disque"   value={systemMetrics.disk}       icon={BarChart3} warn={80} critical={92} />
          <MetricGauge label="Réseau"   value={systemMetrics.network}    icon={Globe}    warn={70} critical={85} />
        </div>

        {/* Health score depuis le store */}
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
          systemHealth.status === 'healthy'  ? 'bg-emerald-950/20 border-emerald-800/30' :
          systemHealth.status === 'warning'  ? 'bg-amber-950/20 border-amber-800/30' :
          'bg-red-950/20 border-red-800/30'
        }`}>
          <CheckCircle size={14} className={
            systemHealth.status === 'healthy' ? 'text-emerald-400' :
            systemHealth.status === 'warning' ? 'text-amber-400' : 'text-red-400'
          } />
          <span className="text-xs text-zinc-300">
            Health score : <span className="font-black">{systemHealth.score ?? '—'}/100</span>
          </span>
          <span className={`text-[10px] uppercase font-bold ml-auto ${
            systemHealth.status === 'healthy' ? 'text-emerald-400' :
            systemHealth.status === 'warning' ? 'text-amber-400' : 'text-red-400'
          }`}>{String(systemHealth.status ?? '')}</span>
        </div>

        {/* Issues — issue peut être string OU { metric, level, value } */}
        {Array.isArray(systemHealth.issues) && systemHealth.issues.length > 0 && (
          <div className="space-y-1">
            {systemHealth.issues.map((issue, i) => {
              const text = typeof issue === 'object' && issue !== null
                ? `${String(issue.metric ?? '?').toUpperCase()} à ${
                    typeof issue.value === 'number' ? issue.value.toFixed(1) : String(issue.value ?? '?')
                  }${typeof issue.value === 'number' && issue.value <= 100 ? '%' : ''}`
                : String(issue ?? '');
              const isCrit = typeof issue === 'object' ? issue?.level === 'critical' : false;
              return (
                <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded border text-xs ${
                  isCrit
                    ? 'bg-red-950/20 border-red-800/20 text-red-300'
                    : 'bg-amber-950/20 border-amber-800/20 text-amber-300'
                }`}>
                  <AlertTriangle size={11} className="shrink-0" />{text}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── KPIs métier — depuis le backend ── */}
      {kpis && (
        <section className="space-y-2">
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2">
            <TrendingUp size={11} /> KPIs plateforme
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <KpiCard label="Utilisateurs"      value={kpis.totalUsers?.toLocaleString()}     icon={Users}   color="text-sky-300"    sub={`+${growth?.newUsers7d ?? 0} cette semaine`} />
            <KpiCard label="Tenants actifs"    value={kpis.totalTenants?.toLocaleString()}   icon={Globe}   color="text-orange-300" sub={`+${growth?.newTenants30d ?? 0} ce mois`} />
            <KpiCard label="Parcours"          value={kpis.totalParcours?.toLocaleString()}  icon={BarChart3} color="text-emerald-300" />
            <KpiCard label="PesaCoins circulant" value={kpis.totalPesas?.toLocaleString()}   icon={Zap}     color="text-amber-300" />
            <KpiCard label="Taux activation"   value={`${kpis.activationRate ?? 0}%`}        icon={Activity} color="text-purple-300" sub={`${kpis.activeUsers7d ?? 0} actifs 7j`} />
            <KpiCard label="Inscrits (24h)"    value={growth?.newUsers1d ?? 0}              icon={Users}   color="text-zinc-200" />
          </div>
        </section>
      )}

      {/* ── Séries temporelles — sparklines ── */}
      <section className="space-y-2">
        <p className="text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2">
          <BarChart3 size={11} /> Tendances 14 jours
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SparkCard label="Inscriptions"  series={series.users}    color="#38bdf8" icon={Users}    />
          <SparkCard label="Nouveaux tenants" series={series.tenants} color="#f97316" icon={Globe}  />
          <SparkCard label="Parcours créés" series={series.parcours} color="#34d399" icon={BarChart3} />
        </div>
      </section>

      {/* ── Répartitions — depuis le backend ── */}
      {distribution && (
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Par plan */}
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-zinc-800/40 text-[10px] uppercase tracking-widest text-zinc-600">
              Tenants par plan
            </div>
            <div className="p-4 space-y-2">
              {['freemium','academia','pro','entreprise'].map(p => {
                const count = distribution.byPlan?.[p] || 0;
                const total = Object.values(distribution.byPlan || {}).reduce((a,b) => a+b, 0) || 1;
                const pct   = Math.round((count / total) * 100);
                const colors = { freemium:'bg-zinc-600', academia:'bg-sky-500', pro:'bg-orange-500', entreprise:'bg-purple-500' };
                return (
                  <div key={p} className="space-y-1">
                    <div className="flex justify-between text-[10px]">
                      <span className="text-zinc-400 font-mono">{p}</span>
                      <span className="text-zinc-500">{count} ({pct}%)</span>
                    </div>
                    <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${colors[p]}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Services live */}
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-zinc-800/40 text-[10px] uppercase tracking-widest text-zinc-600">
              Services
            </div>
            <div className="divide-y divide-zinc-800/20">
              {/* Store : services socket live — guard Array */}
              {Array.isArray(liveServices) && liveServices.map((s, i) => (
                <ServiceRow key={`live-${i}`} service={s} />
              ))}
              {/* Backend : services Node/DB — guard Array */}
              {Array.isArray(services) && services.map((s, i) => (
                <ServiceRow key={`be-${i}`} service={s} />
              ))}
              {liveServices.length === 0 && services.length === 0 && (
                <p className="px-4 py-3 text-zinc-700 text-xs">Aucun service détecté</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Infos système OS — depuis le backend ── */}
      {system && (
        <section className="space-y-2">
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2">
            <Server size={11} /> Système
          </p>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Uptime',   value: `${system.uptimeHours}h` },
              { label: 'CPUs',     value: `${system.cpuCount}× ${system.cpuModel?.split(' ')[0]}` },
              { label: 'RAM',      value: `${system.memUsedMB}/${system.memTotalMB} MB (${system.memUsedPct}%)` },
              { label: 'Platform', value: `${system.platform} ${system.arch}` },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-[9px] text-zinc-600 uppercase tracking-widest">{label}</p>
                <p className="text-xs text-zinc-300 font-mono mt-0.5 truncate" title={value}>{value}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}